<template>
  <div id="app">

    <CategoriesSideMenu
        v-if="ready.cats"
        id="side-menu"
        menu-title="Categorías"
        :categories="categories"
      />

    <!-- Hide reload bar if is not in the app -->
    <vue-loadmore
      :on-refresh="onRefresh"
      :pulling-text="pullingText"
      :refresh-text="refreshText"
      :loosing-text="loosingText"
      :error-text="errorText"
      :head-height="inApp ? 50 : 0"
      :show-success-text=false
    >
      <div id="page">
        <TheNavbar ref="navbar" :auth="isAuth" />
        <div class="app-body" id="appBody">
          <router-view></router-view>
        </div>
        <TheFooter />
      </div>
      <LoginDialog />
      <LocationDialog />
      <SalesDisabledMessageDialog />
      <div ref="dialogs" id="dialogs"></div>
      <Cover />
      <button
        v-if="showSellButton"
        type="button"
        class="btn btn-primary btn-floating rounded-circle d-block d-sm-none"
        :data-toggle="`${isAuth ? '' : 'modal'}`" data-target="#loginModal"
        @click="goSell"
      >
        <font-awesome-icon class="menu-icon d-lg-none" :icon="[ 'fa', 'edit' ]" />
      </button>
    </vue-loadmore>

  </div>
</template>

<script>
import store from '@/store'
import location from '@/location'
import {notifications} from '@/services'
import CategoriesSideMenu from '@/components/categories/SideMenu'
import {instance as actionDialog} from '@/components/layout/ActionDialog'
import {instance as snack} from '@/components/layout/SnackMessage'
import SalesDisabledMessageDialog from '@/components/layout/SalesDisabledMessage'

export default {
  name: 'App',

  metaInfo () {
    return {
      title: this.$SITE_NAME + ' — Clasificados gratis en Argentina',
      link: [
        { rel: 'canonical', href: this.$SITE_URL + this.$route.fullPath }
      ],
      meta: [
        // Twitter - twitter
        { vmid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
        { vmid: 'twitter:site', name: 'twitter:site', content: '@maspocovendo.com' },
        { vmid: 'twitter:title', name: 'twitter:title', content: 'MasPocoVendo.com — Clasificados gratis en Argentina' },
        { vmid: 'twitter:description', name: 'twitter:description', content: 'Sitio de compra venta en Argentina, autos, motos, inmuebles, y todo tipo de productos. Vendé de todo en MasPocoVendo.com' },
        { vmid: 'twitter:image', name: 'twitter:image', content: this.$SITE_URL + '/static/img/logo-256x256.png' },

        { vmid: 'og:site_name', property: 'og:site_name', content: 'MasPocoVendo.com' },
        { vmid: 'og:locale', property: 'og:locale', content: 'es_AR' },
        { vmid: 'og:title', property: 'og:title', content: 'MasPocoVendo.com — Clasificados gratis en Argentina' },
        { vmid: 'og:description', property: 'og:description', content: 'Sitio de compra venta en Argentina, autos, motos, inmuebles, y todo tipo de productos. Vendé de todo en MasPocoVendo.com' },
        { vmid: 'description', name: 'description', content: 'Sitio de compra venta en Argentina, autos, motos, inmuebles, y todo tipo de productos. Vendé de todo en MasPocoVendo.com' },
        { vmid: 'og:url', property: 'og:url', content: this.$SITE_URL + this.$route.fullPath },
        { vmid: 'og:image', property: 'og:image', content: this.$SITE_URL + '/static/img/logo-256x256.png' },
        { vmid: 'og:type', property: 'og:type', content: 'website' },


      ]
    }
  },

  components: {
    CategoriesSideMenu,
    SalesDisabledMessageDialog
  },

  data: () => ({
    /** @var {object[boolean]} ready true when each API request is fullfilled  */
    ready: {
      cats: false
    },
    checkVersionInterval: null,
    pullingText: 'Tirá para refrescar',
    refreshText: 'Refrescando...',
    loosingText: 'Soltá para refrescar...',
    errorText: 'Error al refrescar',
    refreshHeight: 50,
  }),

  computed: {
    /**
     * @return {array} state variable's clone.
     */
    categories () {
      return [...store.state.categories] || []
    },

    /**
     * @return {boolean} indicates user is logged
     */
    isAuth () {
      return store.state.user.isAuth || false
    },

    frontendOutOfDate: () => store.state.frontendOutOfDate,

    frontendUnderMaintenance: () => store.state.maintenance,

    showSellButton: function () {
      return this.$route.name == 'home'
        || this.$route.name == 'category'
    },

    // The vuej-loadmore plugin disable the browser reload functionallity, so we have to enable it only in als
    inApp: () => true // localStorage.getItem('appmobile') && localStorage.getItem('appmobile') == 'true'
  },

  watch: {
    isAuth (status) {
      if (!status && this.$route.meta.requiresAuth) this.$router.push({ name: 'home' })
    },

    frontendOutOfDate (newVal) {
      if (newVal) {
        store.dispatch('setFrontendOutOfDate', false)
        actionDialog({
          statement: 'Estás viendo una versión desactualizada de nuestro sitio. Por favor, actualizá la pestaña del navegador.',
          positiveAction: () => { window.location.reload(true) },
          positiveLabel: 'Actualizar',
          negativeAction: () => {
            snack({
              title: 'Actualizá la pestaña',
              text: 'Tu versión del sitio está desactualizada',
              linkAction: () => { window.location.reload(true) },
              linkText: 'Actualizar',
              timeout: 179000
            })
          },
          negativeLabel: 'Cerrar',
          error: true,
          noClose: true
        })
      }
    },

    /**
     * If store variable maintenance is set to true from the api interceptor, show dialog blocking the UI
     */
    frontendUnderMaintenance (newVal) {
      if (newVal) {
        actionDialog({
          statement: 'Estamos actualizando nuestro sitio. Por favor, esperá unos minutos y actualizá la pestaña del navegador.',
          positiveAction: () => { window.location.reload(true) },
          positiveLabel: 'Actualizar',
          maintenance: true,
          noClose: true
        })
      }
    }
  },

  methods: {

    /**
     * Fetch API data & populate the state.
     *
     * @uses Vuex.dispatch
     */
    getInitialValues () {
      store.dispatch('fetchCategories').then(res => { this.ready.cats = true })
      // store.dispatch('getUserAgent')
      this.loginFromLocalStorage().then(() => location.init())
    },

    /**
     * Gets token from localStorage and login.
     *
     * @uses localStorage
     * @uses dispatch to login
     *
     * @return {boolean} whether is auth or not.
     */
    loginFromLocalStorage () {
      if (!window.localStorage) {
        console.log('LocalStorage not supported')
        return Promise.resolve(false)
      }

      const token = window.localStorage.getItem('bearer')
      const refresh = window.localStorage.getItem('refresh')

      if (!token) {
        return Promise.resolve(false)
      }

      store.dispatch('user/setTokens', {token, refresh})

      return Promise.resolve()
    },
    goSell () {
      this.$refs.navbar.goSell()
    },

    onRefresh (done) {
      // Enable refresh only in app
      if(this.inApp) {
        this.$router.go();
      }
      done();
    },

  },

  created () {
    // fetches data from API
    this.getInitialValues()
    this.checkVersionInterval = setInterval(() => { store.dispatch('setVersionChecked', false) }, 180000)
  },

  mounted () {
    // ----------sets infinite scroll event ---------- //
    const event = new CustomEvent('scrollEnding', {bubbles: true})

    const detectComingEnd = perc => (e) => {
      const offset = window.scrollY
      const max = document.body.offsetHeight
      const win = window.innerHeight

      if (offset + win >= (max * (perc || 1))) dispatchEvent(event)
    }
    // check: document.body.addEventListener(...)
    addEventListener('scroll', detectComingEnd(0.7))
    // -------- end infinite scroll event ---------- //

    if (store.state.user.isAuth && this.$route.name !== 'userProfile') {
      store.dispatch('user/fetchUserInfo')
        .then(notifications.init)
    }

    // // Enable refresh only in app
    // if(localStorage.getItem('appmobile') == 'true') {
    //   this.refreshHeight = 50
    // }

    // store.dispatch('showSecurityNotice')
  },

  beforeDestroy () {
    clearInterval(this.checkVersionInterval)
  }
}
</script>

<style lang="scss">
  @import 'scss/breakpoints';
  @import 'scss/main';

  * {
    font-family: 'Roboto-local', sans-serif;
  }

  @font-face {
    font-family: 'Roboto-local';
    src: url(/static/fonts/Roboto-Light.woff2) format('woff2'),
      url(/static/fonts/Roboto-Light.woff) format('woff'),
      url(/static/fonts/Roboto-Light.ttf) format('truetype');
  }

  .app-body {
    min-height: 100vh;
  }

  body {
    overflow-x: hidden;
    // height: 100%;
  }

  .btn {
    border-radius: 3px;
  }

  .status-bar {
    top: 50px;
  }

  .btn-floating {
    position: fixed;
    top: 89%;
    right: 7%;
    box-shadow: 4px 4px 10px #888888;
    width: 7vw;
    height: 7vw;
    min-width: 50px;
    min-height: 50px;
    z-index: 1;
  }

  @media screen and (max-width: map-get($grid-breakpoints, "sm")) {
    html {
      overflow-x: hidden;
    }

    body {
      font-size: 1.125rem;
    }

    // Increase buttons size across app
    .btn {
      line-height: 2;
    }

    // Unsets input field height to match button height
    .form-control {
      height: unset;
    }
  }
</style>



// WEBPACK FOOTER //
// src/App.vue